import { useState, type ReactNode } from "react";
import { NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import {
  CalendarDays, ChartNoAxesCombined, CircleDollarSign, ClipboardList, Clock3, LayoutDashboard, LogOut, Menu, MessageCircle,
  Trophy, GraduationCap, Tags, Package, PanelLeftClose, TableProperties, Users, Volleyball, Warehouse, X,
} from "lucide-react";
import { useAuth } from "../auth";
import { Logo } from "./Logo";

const navigation: Array<{ to: string; label: string; icon: typeof LayoutDashboard; end?: boolean; roles?: string[] }> = [
  { to: "/painel", label: "Visão geral", icon: LayoutDashboard, end: true },
  { to: "/painel/erp", label: "ERP · Gestão", icon: ChartNoAxesCombined, roles: ["OWNER", "ADMIN"] },
  { to: "/painel/reservas", label: "Reservas e créditos", icon: CalendarDays, roles: ["OWNER", "ADMIN", "RECEPTION", "CASHIER"] },
  { to: "/painel/agenda", label: "Agenda", icon: CalendarDays },
  { to: "/painel/funcionamento", label: "Funcionamento", icon: Clock3, roles: ["OWNER", "ADMIN"] },
  { to: "/painel/aulas", label: "Aulas", icon: GraduationCap, roles: ["OWNER", "ADMIN"] },
  { to: "/painel/campeonatos", label: "Campeonatos", icon: Trophy, roles: ["OWNER", "ADMIN"] },
  { to: "/painel/quadras", label: "Quadras", icon: Volleyball },
  { to: "/painel/clientes", label: "Clientes", icon: Users },
  { to: "/painel/mesas", label: "Mesas e pedidos", icon: TableProperties },
  { to: "/painel/produtos", label: "Produtos", icon: Package },
  { to: "/painel/categorias", label: "Categorias", icon: Tags, roles: ["OWNER", "ADMIN"] },
  { to: "/painel/estoque", label: "Estoque", icon: Warehouse, roles: ["OWNER", "ADMIN"] },
  { to: "/painel/caixa", label: "Caixa", icon: CircleDollarSign },
  { to: "/painel/atendimento", label: "Atendimento", icon: MessageCircle, roles: ["OWNER", "ADMIN", "RECEPTION"] },
  { to: "/painel/whatsapp", label: "WhatsApp", icon: MessageCircle },
];

export function ProtectedLayout() {
  const { user, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  if (loading) return <div className="app-loader"><span className="spinner" /><Logo /></div>;
  if (!user) return <Navigate to="/entrar" replace state={{ from: location.pathname }} />;
  return (
    <div className="app-shell">
      <aside className={`sidebar ${open ? "sidebar-open" : ""}`}>
        <div className="sidebar-brand"><Logo /><button className="icon-button mobile-only" aria-label="Fechar menu" onClick={() => setOpen(false)}><X /></button></div>
        <nav className="sidebar-nav">
          {navigation.filter((item) => !item.roles || item.roles.includes(user.role)).map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} className={({ isActive }) => isActive ? "active" : ""}>
              <Icon size={20} /><span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-user">
          <span className="avatar">{user.name.slice(0, 2).toUpperCase()}</span>
          <span><strong>{user.name}</strong><small>{roleLabel(user.role)}</small></span>
          <button className="icon-button" onClick={logout} title="Sair"><LogOut size={18} /></button>
        </div>
      </aside>
      <div className="app-content">
        <header className="mobile-header"><button className="icon-button" aria-label="Abrir menu" onClick={() => setOpen(true)}><Menu /></button><Logo compact /></header>
        <main><Outlet /></main>
      </div>
      {open && <button className="sidebar-backdrop" onClick={() => setOpen(false)} aria-label="Fechar menu" />}
    </div>
  );
}

function roleLabel(role: string): string {
  return ({ OWNER: "Proprietário", ADMIN: "Administrador", RECEPTION: "Recepção", CASHIER: "Caixa", WAITER: "Garçom" } as Record<string, string>)[role] ?? role;
}

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: string; title: string; description?: string; actions?: ReactNode }) {
  return <div className="page-header"><div>{eyebrow && <span className="eyebrow">{eyebrow}</span>}<h1>{title}</h1>{description && <p>{description}</p>}</div>{actions && <div className="page-actions">{actions}</div>}</div>;
}

export function EmptyState({ icon: Icon = ClipboardList, title, description, action }: { icon?: typeof ClipboardList; title: string; description: string; action?: ReactNode }) {
  return <div className="empty-state"><span><Icon /></span><h3>{title}</h3><p>{description}</p>{action}</div>;
}

export function Modal({ title, children, onClose, wide = false, className = "" }: { title: string; children: ReactNode; onClose: () => void; wide?: boolean; className?: string }) {
  return <div className="modal-layer" role="dialog" aria-modal="true" aria-label={title}><button className="modal-backdrop" onClick={onClose} aria-label="Fechar" /><section className={`modal ${wide ? "modal-wide" : ""} ${className}`}><header><h2>{title}</h2><button className="icon-button" onClick={onClose} aria-label="Fechar"><X /></button></header><div className="modal-body">{children}</div></section></div>;
}
